import { cn, Kbd, LabelCaps } from '@pm/ui';
import { Search, BellRing, Settings2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMe } from '@/lib/auth';
import { navItemsFor, type NavItem } from './nav';

/**
 * Command palette (⌘K). Fase 0: navegação. Demandas, clientes e ações entram com os módulos.
 * O pai remonta o diálogo a cada abertura (key), então o estado inicial já é limpo.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return <PaletteDialog onClose={onClose} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const items = useMemo<NavItem[]>(
    () => [
      ...navItemsFor(me),
      { to: '/settings/appearance', label: 'Aparência', icon: Settings2 },
      { to: '/settings/notifications', label: 'Notificações', icon: BellRing },
    ],
    [me],
  );
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  }, [items, query]);

  function go(item: NavItem) {
    onClose();
    void navigate(item.to);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      const item = results[active];
      if (item) go(item);
    }
  }

  return (
    <div
      className="fixed inset-0 z-(--z-modal) flex items-start justify-center bg-[var(--scrim)] px-4 pt-[20vh]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal
        aria-label="Buscar ou comando"
        className="glass w-full max-w-[560px] overflow-hidden rounded-lg shadow-overlay"
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-2.5 border-b border-glass-border px-3.5 py-3">
          <Search className="size-4 text-text-3" aria-hidden />
          <input
            autoFocus
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Ir para…"
            className="flex-1 bg-transparent text-[15px] text-text-1 outline-none placeholder:text-text-3"
          />
          <Kbd>Esc</Kbd>
        </div>
        <div className="flex flex-col gap-0.5 p-2">
          <LabelCaps className="px-2 pb-1 pt-1.5">Ir para</LabelCaps>
          {results.length === 0 ? (
            <p className="px-2 py-3 text-sm text-text-2">Nada encontrado para “{query}”.</p>
          ) : null}
          {results.map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={item.to}
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => go(item)}
                className={cn(
                  'flex h-[30px] items-center gap-2.5 rounded px-2 text-left text-base text-text-1',
                  i === active && 'bg-accent-soft',
                )}
              >
                <Icon className="size-4 text-text-2" aria-hidden />
                <span className="flex-1">{item.label}</span>
                {item.shortcut ? <Kbd>{item.shortcut}</Kbd> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
